
import React from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addDays, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';

interface WeekDay {
  key: string;
  name: string;
  initial: string;
  date: Date;
}

interface WeeklyMenuNavigationProps {
  currentWeekStart: Date;
  activeWeek: 'current' | 'next';
  selectedDate: Date;
  currentWeekDays: WeekDay[];
  nextWeekDays: WeekDay[];
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onWeekChange: (week: 'current' | 'next') => void;
  onDateSelect: (date: Date) => void;
}

const WeeklyMenuNavigation: React.FC<WeeklyMenuNavigationProps> = ({
  currentWeekStart,
  activeWeek,
  selectedDate,
  currentWeekDays,
  nextWeekDays,
  onPreviousWeek,
  onNextWeek,
  onWeekChange,
  onDateSelect
}) => {
  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <Button 
          variant="outline" 
          size="icon" 
          onClick={onPreviousWeek}
          className="border-vegana-primary text-vegana-primary hover:bg-vegana-primary hover:text-white"
        >
          <ChevronLeft size={18} />
        </Button>
        <h2 className="text-lg font-semibold">
          {format(currentWeekStart, "d MMM", { locale: es })} - {format(addDays(currentWeekStart, 13), "d MMM yyyy", { locale: es })}
        </h2>
        <Button 
          variant="outline" 
          size="icon" 
          onClick={onNextWeek}
          className="border-vegana-primary text-vegana-primary hover:bg-vegana-primary hover:text-white"
        >
          <ChevronRight size={18} />
        </Button>
      </div>
      
      <Tabs 
        defaultValue="current" 
        className="w-full" 
        value={activeWeek}
        onValueChange={(value) => onWeekChange(value as 'current' | 'next')}
      >
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="current">
            {format(currentWeekStart, "d MMM", { locale: es })} - {format(addDays(currentWeekStart, 6), "d MMM", { locale: es })}
          </TabsTrigger>
          <TabsTrigger value="next">
            {format(addDays(currentWeekStart, 7), "d MMM", { locale: es })} - {format(addDays(currentWeekStart, 13), "d MMM", { locale: es })}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="current" className="mt-0">
          <div className="flex justify-center py-4">
            <div className="grid grid-cols-7 gap-1 md:gap-3 w-full max-w-5xl">
              {currentWeekDays.map((day) => (
                <button
                  key={day.key}
                  onClick={() => onDateSelect(day.date)}
                  className={`flex flex-col items-center justify-center rounded-xl p-2 transition-all ${
                    isSameDay(day.date, selectedDate) && activeWeek === 'current'
                      ? 'bg-vegana-primary text-white scale-105 shadow-lg' 
                      : 'bg-white text-vegana-primary hover:bg-vegana-primary/10'
                  }`}
                >
                  <span className="text-[9px] md:text-xs font-medium truncate">{day.name.substring(0, 3)}</span>
                  <span className="text-sm md:text-lg font-bold">{day.initial}</span>
                  <span className="text-[9px] md:text-xs">{format(day.date, 'd', { locale: es })}</span>
                </button>
              ))}
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="next" className="mt-0">
          <div className="flex justify-center py-4">
            <div className="grid grid-cols-7 gap-1 md:gap-3 w-full max-w-5xl">
              {nextWeekDays.map((day) => (
                <button
                  key={day.key}
                  onClick={() => onDateSelect(day.date)}
                  className={`flex flex-col items-center justify-center rounded-xl p-2 transition-all ${
                    isSameDay(day.date, selectedDate) && activeWeek === 'next'
                      ? 'bg-vegana-primary text-white scale-105 shadow-lg' 
                      : 'bg-white text-vegana-primary hover:bg-vegana-primary/10'
                  }`}
                >
                  <span className="text-[9px] md:text-xs font-medium truncate">{day.name.substring(0, 3)}</span>
                  <span className="text-sm md:text-lg font-bold">{day.initial}</span>
                  <span className="text-[9px] md:text-xs">{format(day.date, 'd', { locale: es })}</span>
                </button>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WeeklyMenuNavigation;
